import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { getBrightcovePlayerInfo } from '../services/UtilityService'

const BrightcoveVideo = ({
  videoPlayerId,
  videoPlayerListId,
  videoPlayerType = 'Single',
  disablePlayButton = false,
}) => {
  if (!videoPlayerId || !videoPlayerListId) {
    console.error('Missing required props')
    return null
  }
  const videoRef = useRef(null)
  const bigPlayButtonRef = useRef(null)
  const { dataAccountId, dataPlayerId } = getBrightcovePlayerInfo(videoPlayerId)
  const isPlaylist = videoPlayerType?.toLowerCase() === 'playlist'
  const playerRef = useRef(null)


  const loadBrightcoveScript = () => {
    return new Promise((resolve, reject) => {
      const scriptId = `bc-script-${dataAccountId}-${dataPlayerId}`

      if (document.getElementById(scriptId)) {
        if (window.bc && window.videojs) {
          resolve()
        } else {
          const checkInterval = setInterval(() => {
            if (window.bc && window.videojs) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 50)
        }
        return
      }

      const script = document.createElement('script')
      script.id = scriptId
      script.src = `https://players.brightcove.net/${dataAccountId}/${dataPlayerId}_default/index.min.js`
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Brightcove script'))
      document.body.appendChild(script)
    })
  }

  useEffect(() => {
    let player

    const initPlayer = async () => {
      try {
        await loadBrightcoveScript()

        if (videoRef.current) {
          player = window.bc(videoRef.current)
          window.videojs(videoRef.current)
          playerRef.current = player
          console.log('✅ Brightcove player initialized')

          let userInteracted = false

          player.ready(() => {
            const buttonEl = player.el().querySelector('.vjs-big-play-button')
            if (buttonEl) {
              bigPlayButtonRef.current = buttonEl
              const handleUserInteraction = () => {
                userInteracted = true
                console.log('User interacted → allow playback')
              }

              buttonEl.addEventListener('click', handleUserInteraction, { once: true })
              buttonEl.addEventListener('touchstart', handleUserInteraction, { once: true })
              buttonEl.addEventListener('keydown', handleUserInteraction, { once: true })
              videoRef.current.addEventListener('click', handleUserInteraction, { once: true })
            }
          })

          // 🔹 When this player starts playing → broadcast event
          player.on('play', () => {
            if (!userInteracted) {
              console.log('❌ Autoplay detected → stopping video')
              player.pause()
              player.load()
              return
            }

            console.log('▶️ Player started, pausing others')
            window.dispatchEvent(new CustomEvent('brightcove-playing', { detail: player.id() }))
          })

          // 🔹 Listen for other players starting
          const handleOtherPlayerPlay = (e) => {
            if (e.detail !== player.id()) {
              console.log(`⏸ Pausing this player (${player.id()}) because another started`)
              player.pause()
            }
          }
          window.addEventListener('brightcove-playing', handleOtherPlayerPlay)

          // Cleanup
          return () => {
            window.removeEventListener('brightcove-playing', handleOtherPlayerPlay)
            player.dispose?.()
          }
        }
      } catch (err) {
        console.error('Brightcove init failed', err)
      }
    }

    const cleanup = initPlayer()
    return () => {
      if (typeof cleanup === 'function') cleanup()
    }
  }, [dataAccountId, dataPlayerId, videoPlayerListId, isPlaylist])

  return (
    <div
      className={`vjs-playlist-player-container ${isPlaylist ? 'vjs-playlist-player-container--playlist' : ''
        } ${disablePlayButton ? 'vjs-playlist-player-container--disabled' : ''}`}
    >
      <video
        id={`brightcove-player-${videoPlayerId}`}
        ref={videoRef}
        data-testid="videoPlayer"
        data-account={dataAccountId}
        data-player={dataPlayerId}
        data-embed="default"
        data-video-id={!isPlaylist ? videoPlayerListId : undefined}
        data-playlist-id={isPlaylist ? videoPlayerListId : undefined}
        className="video-js"
        controls
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

BrightcoveVideo.propTypes = {
  videoPlayerType: PropTypes.oneOf(['Single', 'Playlist']),
  disablePlayButton: PropTypes.bool,
}

export default BrightcoveVideo
